import { createPortal } from "react-dom";
import { Compass, GraduationCap, HelpCircle } from "lucide-react";
import { Btn } from "../../components/ui";

/**
 * Willkommen beim ersten Login (Admins): zeigt, wo die Anleitungen zu finden sind,
 * und erklärt Platzhalter. Ersetzt für Admins die allgemeine Tour-Frage.
 */
export function WillkommenDialog({
  onOrientation,
  onStartTutorial,
  onLater,
}: {
  onOrientation: () => void;
  onStartTutorial: () => void;
  onLater: () => void;
}) {
  const wege = [
    { icon: <GraduationCap className="h-[18px] w-[18px]" />, title: "Erste Schritte im Dashboard", text: "Mitmach-Tutorials zu QM-Handbuch, Verfahrensanweisungen, Mitarbeitenden, Fortbildungen und Verbesserungsliste – du klickst selbst, wir zeigen dir wo." },
    { icon: <HelpCircle className="h-[18px] w-[18px]" />, title: "Hilfe – oben rechts (?)", text: "Alle Anleitungen und die Mitmach-Tutorials, jederzeit erreichbar." },
    { icon: <Compass className="h-[18px] w-[18px]" />, title: "Kompass – oben rechts", text: "Erklärt dir die Seite, auf der du gerade bist." },
  ];
  return createPortal(
    <div className="fixed inset-0 z-[76] flex items-center justify-center overflow-y-auto bg-slate-900/60 p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="willkommen-titel" className="my-auto w-full max-w-lg rounded-[16px] border border-[var(--line)] bg-white p-6 shadow-[0_24px_60px_rgba(16,32,38,.28)]">
        <div id="willkommen-titel" className="text-lg font-extrabold tracking-[-.02em] text-[var(--ink)]">Willkommen bei Sonne QM 👋</div>
        <p className="mt-1.5 text-sm leading-relaxed text-[var(--ink-mid)]">Schön, dass du da bist! Hier findest du deine Anleitungen:</p>

        <ul className="mt-4 grid gap-3">
          {wege.map((w) => (
            <li key={w.title} className="flex gap-3">
              <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[9px] bg-[var(--teal-light)] text-[var(--teal-deep)]">{w.icon}</span>
              <span className="min-w-0">
                <strong className="block text-[.86rem] text-[var(--ink)]">{w.title}</strong>
                <span className="block text-[.8rem] leading-relaxed text-[var(--ink-muted)]">{w.text}</span>
              </span>
            </li>
          ))}
        </ul>

        <div className="mt-5 rounded-[12px] border border-amber-200 bg-amber-50 p-3.5">
          <div className="text-[.84rem] font-bold text-amber-900">So funktionieren Platzhalter</div>
          <p className="mt-1 text-[.8rem] leading-relaxed text-amber-900/90">
            Unsere Vorlagen enthalten Platzhalter wie <code className="rounded bg-white/70 px-1">{"{{PFLEGEDIENST_NAME}}"}</code> oder{" "}
            <code className="rounded bg-white/70 px-1">{"{{NAME_QMB}}"}</code>. Beim Übernehmen setzt Sonne QM automatisch deine Daten ein:
            aus dem <strong>Profil</strong> deines Pflegedienstes („Organisation“) und aus den <strong>Rollen</strong> deiner
            Mitarbeitenden (Qualifikation, z. B. PDL, QMB, Hygienebeauftragte/r). Was noch fehlt, bleibt als {"{{…}}"} stehen und lässt
            sich später ergänzen.
          </p>
          <p className="mt-1.5 text-[.8rem] font-semibold text-amber-900">
            Tipp: Erst Profil und Rollen pflegen, dann Vorlagen übernehmen – dann ist alles direkt befüllt.
          </p>
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
          <Btn variant="secondary" onClick={onLater}>Später</Btn>
          <Btn variant="secondary" onClick={onOrientation}>Zeig mir, wo alles ist</Btn>
          <Btn onClick={onStartTutorial}>Erstes Tutorial starten</Btn>
        </div>
      </div>
    </div>,
    document.body
  );
}
